import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Row, Col } from 'react-flexbox-grid'
import isEmail from 'validator/es/lib/isEmail'
import set from 'lodash/set'

import Schedule from './Schedule'
import { ContentBlock } from 'components/ContentBlock'
import { Form, Text, Number as NumberInput, Checkbox } from 'components/forms'
import { notEmpty } from 'helpers/validators'
import listTasksQuery from '../../gql/listAutomationTaskServer'
import updateEnabilityMutation from '../../gql/updateAutomationTaskServerEnability'
import convertTaskMutation from '../../gql/convertAutomationTaskServerFromWindows'
import fetchedDataToForm from 'helpers/fetchedDataToForm'
import getQueryName from 'helpers/getNameOfGqlQuery'
import { useMutation } from 'components/graphql'
import Button from 'components/Button'
import Divider from 'components/Divider'

const ServerTask = ({ data, itemId, mutations, newRecord, handleNewRecord }) => {
	const [isActive, setIsActive] = useState(data?.isActive)
	const [updateEnability] = useMutation(updateEnabilityMutation, {
		onError: err => {
			setIsActive(!isActive)
			throw new Error(err)
		}
	})
	const [convertTask, { loading: convertLoading }] = useMutation(convertTaskMutation, {
		onCompleted: mutationData => {
			const mutationName = getQueryName(convertTaskMutation)
			handleNewRecord(mutationData?.[mutationName])
		}
	})
	const [t, { language }] = useTranslation()

	const formData = useMemo(() => fetchedDataToForm(data, [], true), [data])

	const handleSubmit = vals => {
		if(newRecord) {
			mutations.createItem({
				...vals,
				typeId: 1009
			}, {
				update: (cache, { data: mutationData }) => {
					const cachedData = cache.readQuery({ query: listTasksQuery })
					const queryName = getQueryName(listTasksQuery)
					const cacheList = cachedData?.cashAssist?.[queryName]
					const createdItem = mutationData?.createMutation
					if(Array.isArray(cacheList) && createdItem) {
						const newList = [...cacheList, createdItem]
						set(cachedData, ['cashAssist', queryName], newList)
						cache.writeQuery({ query: listTasksQuery, data: cachedData })
						handleNewRecord(createdItem)
					}
				}
			})
		} else {
			mutations.updateItem(vals)
		}
	}

	const handleActiveChange = e => {
		const val = e.target.checked
		setIsActive(val)
		const { id, typeId } = data || {}
		updateEnability({
			variables: {
				id,
				typeId,
				isActive: val
			}
		})
	}

	const handleConvert = () => {
		convertTask({
			variables: { id: itemId }
		})
	}

	if(data?.isWindows) {
		return(
			<ContentBlock>
				<p>{t('options_section.strings.convert_task')}</p>
				<Divider />
				<Button
					text={t('options_section.strings.convert')}
					onClick={handleConvert}
					loading={convertLoading}
				/>
			</ContentBlock>
		)
	}

	return(
		<ContentBlock>
			<Form initialValues={formData} onSubmit={handleSubmit} smartSubmit={!newRecord}>
				<Row>
					<Col xs>
						<Text
							field='name'
							label={t('common.fields.name')}
							{...notEmpty()}
							validateOnChange
						/>
						<Text
							field='params.email'
							label={t('common.fields.email')}
							validate={val => {
								if(!isEmail(val || '')) {
									return t('validators.invalid_value')
								}
							}}
							validateOnChange
						/>
					</Col>
					<Col xs>
						<NumberInput
							field='params.daysBack'
							label={t('options_section.fields.days_back')}
							shrink
						/>
						<Checkbox
							noForm
							checked={!newRecord ? isActive : true}
							onChange={handleActiveChange}
							disabled={!!newRecord}
							label={t('common.fields.isActive')}
							inline
						/>
					</Col>
				</Row>
				<Divider visible />
				<Schedule
					data={data?.params?.schedule}
					newRecord={newRecord}
					t={t}
					language={language}
				/>
				{newRecord && (
					<>
						<Divider />
						<Button type='submit' text={t('common.buttons.create')} />
					</>
				)}
			</Form>
		</ContentBlock>
	)
}

export default ServerTask
